import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";

import { sanitizeCountryCode } from "@expert/common-ui";
import { Container, Stack, Text } from "@mantine/core";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useAnalytics } from "../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { useVerifiedCustomerStore } from "../../../../stores";
import { IneligibleIcon } from "../../../product-sale-flow/components/Icons";
import { SimplifiedMdnSection } from "../customer-verification/SimplifiedMdnSection";
import classes from "./CustomerOffers.module.css";

interface Props {
    sessionId: string;
}

/**
 * Shown when the eligibility API rejects the request with an invalid MDN (400).
 * Substitutes the generic "try refreshing" error view with an inline phone-number
 * input. Saving a corrected MDN updates the verified customer (part of the
 * eligibility queryKey), which re-runs the eligibility check automatically.
 */
export function InvalidMdnView({ sessionId }: Props) {
    const { partnerConfig, isSimulatedVoiceTask } = useSalesContext();
    const { dispatcher: salesDispatcher } = useAnalytics();
    const dispatcher = salesDispatcher as AbstractAnalyticsDispatcher;

    const customerMdn = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer?.mdn);

    const {
        features: { isInternationalPhoneEnabled = false, defaultCountry = "US" },
        blocklistedNumbers,
        blocklistedPrefixes,
    } = partnerConfig;

    return (
        <Stack align="center" data-testid="invalid-mdn" m="lg">
            <Container>{IneligibleIcon}</Container>
            <Text className={classes.statusBodyHeader}>That phone number isn’t valid.</Text>
            <SimplifiedMdnSection
                key={sessionId}
                initialMdn={customerMdn ?? ""}
                isEditing
                heading="Double-check the phone number and re-enter it."
                onComplete={(mdn) => useVerifiedCustomerStore.getState().setCustomer(sessionId, { mdn })}
                onEditClick={() => {}}
                shouldAutoFocus
                isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                shouldSkipValidation={isSimulatedVoiceTask}
                defaultCountry={sanitizeCountryCode(defaultCountry)}
                dispatcher={dispatcher}
                blocklistedNumbers={blocklistedNumbers}
                blocklistedPrefixes={blocklistedPrefixes}
            />
        </Stack>
    );
}
